/**
 * Fetching the roster and claiming a row (D59).
 *
 * Separate from `session.ts` because this is a one-time flow at the welcome
 * screen, and separate from `roster.ts` because that file is pure ranking with
 * no network in it.
 */
import type { ClaimFailure, ClaimResponse, RosterEntry, RosterResponse } from '@mv/contracts';
import { ApiError, api } from './api';
import { readTokens, refresh } from './tokens';

const CACHE_KEY = 'mv.roster';

/**
 * The whole list, cached.
 *
 * **The cache is what makes the picker survive the connection**, which is the
 * point of shipping the list rather than a `?q=` endpoint. A guest standing at
 * check-in on resort wifi who loses the network mid-pick still has every name
 * in front of them.
 *
 * Not keyed by `sub`, unlike `mv.profile`: the roster is the same list for
 * everybody, holds no claim information, and is already readable by any signed
 * in guest. There is nothing here for a second guest on a shared phone to see
 * that they could not fetch themselves.
 */
export async function loadRoster(): Promise<RosterEntry[]> {
  try {
    const { guests } = await api<RosterResponse>('/roster');
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(guests)); } catch { /* private mode */ }
    return guests;
  } catch (err) {
    const cached = cachedRoster();
    // Rethrow only when there is nothing to fall back to — F8. A stale list is
    // wrong in exactly one way (a name added since it was cached), and the
    // escalation path covers that guest. An empty picker covers nobody.
    if (!cached.length) throw err;
    return cached;
  }
}

export function cachedRoster(): RosterEntry[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) as RosterEntry[] : [];
  } catch {
    return [];
  }
}

export type ClaimOutcome =
  | { ok: true; result: ClaimResponse; refreshed: boolean }
  | { ok: false; reason: ClaimFailure | 'network' };

/**
 * Claim a row, then mint a token that carries it.
 *
 * **The refresh is reported, never thrown.** A failed refresh is not a failed
 * claim: the row is latched in DynamoDB the moment the API answers, and
 * `pre-token-gen` stamps `custom:guestId` on every subsequent sign-in. So the
 * guest is claimed and merely holding a stale token — recoverable by signing
 * out and back in, unaided (F6). Treating that as a failure would send them
 * back to the picker to re-claim a row they already hold, which is the one
 * place the idempotent condition saves us and the one place it should not have
 * to.
 */
export async function claimName(guestId: string): Promise<ClaimOutcome> {
  let result: ClaimResponse;
  try {
    result = await api<ClaimResponse>('/me/claim', { method: 'POST', json: { guestId } });
  } catch (err) {
    if (err instanceof ApiError && isClaimFailure(err.code)) {
      return { ok: false, reason: err.code };
    }
    // Network, gateway, anything else. F4: the picker stays live behind a
    // retry, and a retry is safe because the write is idempotent for this
    // caller — if the first attempt did land, the second one succeeds (F3).
    return { ok: false, reason: 'network' };
  }

  // **Caught here rather than inside `refresh`, deliberately.** `refresh` calls
  // `fetch` with no guard, so a connection dropping between the claim landing
  // and this call throws — and uncaught it escapes `claimName`, leaving the
  // picker on "Matching you up…" forever with the claim already succeeded.
  //
  // The fix does not belong in `refresh` itself: `currentIdToken` treats a
  // `null` from it as "the refresh token is gone" and *clears the session*, so
  // teaching `refresh` to answer `null` on a network error would sign guests
  // out on a blip. That is a worse bug than the one being fixed, and a common
  // one at a resort. So the network failure is absorbed at the one call site
  // that knows a failed refresh is not a failed claim.
  let refreshed = false;
  try {
    const tokens = readTokens();
    refreshed = tokens ? (await refresh(tokens)) !== null : false;
  } catch {
    // F6. The row is latched; the token is merely stale.
  }
  return { ok: true, result, refreshed };
}

const CLAIM_FAILURES: ClaimFailure[] = ['taken', 'held-elsewhere', 'unknown-guest'];
const isClaimFailure = (code: string): code is ClaimFailure =>
  (CLAIM_FAILURES as string[]).includes(code);

/** Give a row back. Idempotent server-side, so this needs no guard. */
export async function releaseName(): Promise<void> {
  await api<void>('/me/claim', { method: 'DELETE' });
}

/**
 * "I can't find myself" (F7).
 *
 * Returns whether it was filed. **It must not report success it did not have**
 * — four other failure modes terminate here, and a screen that says a request
 * was sent when it was not is worse than one that admits it could not. The
 * caller's fallback needs no network at all.
 */
export async function requestClaimHelp(note: string): Promise<boolean> {
  try {
    await api<void>('/me/claim/help', { method: 'POST', json: { note } });
    return true;
  } catch {
    return false;
  }
}
